import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import styles from './applications.module.css';

// Import base64 encoded font
import robotoBase64 from '../fonts/Roboto/Roboto-Regular.base64.txt'; // Update this path if needed

const ApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const q = query(collection(db, 'applications'), orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(q);
        const fetchedApplications = [];
        const fetchedPositions = new Set();

        for (const docRef of querySnapshot.docs) {
          const application = docRef.data();
          const userId = application.userId;
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            fetchedApplications.push({
              ...application,
              user: {
                email: userData.email,
                firstname: userData.firstName,
                lastname: userData.lastName,
                birthdate: userData.birthDate,
                phone: userData.phone,
              },
            });

            fetchedPositions.add(application.position);
          }
        }

        setApplications(fetchedApplications);
        setPositions(Array.from(fetchedPositions));
      } catch (error) {
        console.error('Error fetching applications: ', error);
      }
    };

    fetchApplications();
  }, []);

  const handlePositionSelect = (position) => {
    setSelectedPosition(position);
  };

  const [expandedRows, setExpandedRows] = useState({});

  const handleToggleExpand = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Function to create a safe filename
  

  // Function to export data to PDF
  const turkishToEnglishMap = {
    'ş': 's',
    'Ş': 'S',
    'ı': 'i',
    'İ': 'I',
    'ç': 'c',
    'Ç': 'C',
    'ğ': 'g',
    'Ğ': 'G',
    'ü': 'u',
    'Ü': 'U',
    'ö': 'o',
    'Ö': 'O'
  };
  
  // Function to replace Turkish characters with English equivalents
  // Function to replace Turkish characters with English equivalents
const convertTurkishToEnglish = (text) => {
  return text.split('').map(char => turkishToEnglishMap[char] || char).join('');
};

// Function to create a safe filename
const createSafeFilename = (name) => {
  const englishName = convertTurkishToEnglish(name);
  return englishName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
};

const exportToExcel = () => {
  const filteredApplications = applications.filter(
    (app) => app.position === selectedPosition
  );

  if (Array.isArray(filteredApplications)) {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredApplications.map((app) => ({
        'İsim Soyisim': `${app.user.firstname || ''} ${app.user.lastname || ''}`,
        'Doğum Tarihi': app.user.birthdate || 'N/A',
        'Email': app.user.email || 'N/A',
        'Telefon Numarası': app.user.phone || 'N/A',
        'Pozisyon': app.position || 'N/A',
        'Başvuru Tarihi': app.timestamp && app.timestamp.toDate
          ? app.timestamp.toDate().toLocaleDateString()
          : 'N/A',
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');
    const fileName = `${createSafeFilename(selectedPosition)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  } else {
    console.error('Expected applications to be an array');
  }
};

const exportToPDF = () => {
  const filteredApplications = applications.filter(
    (app) => app.position === selectedPosition
  );

  if (Array.isArray(filteredApplications)) {
    const doc = new jsPDF();
    doc.setFont('Helvetica'); // Use a default font

    // Prepare data for PDF with converted characters
    const data = filteredApplications.map((app) => [
      `${convertTurkishToEnglish(app.user.firstname || '')} ${convertTurkishToEnglish(app.user.lastname || '')}`,
      convertTurkishToEnglish(app.user.birthdate || 'N/A'),
      convertTurkishToEnglish(app.user.email || 'N/A'),
      convertTurkishToEnglish(app.user.phone || 'N/A'),
      convertTurkishToEnglish(app.position || 'N/A'),
      app.timestamp && app.timestamp.toDate
        ? convertTurkishToEnglish(app.timestamp.toDate().toLocaleDateString())
        : 'N/A',
    ]);

    // Create PDF table
    doc.autoTable({
      head: [
        [
          'Isim Soyisim',
          'Dogum Tarihi',
          'Email',
          'Telefon Numarasi',
          'Pozisyon',
          'Basvuru Tarihi',
        ],
      ],
      body: data,
    });

    // Save PDF
    const fileName = `${createSafeFilename(selectedPosition)}.pdf`;
    doc.save(fileName);
  } else {
    console.error('Expected applications to be an array');
  }
};

  
  return (
    <div className={styles.container}>
      {/* Sidebar for Positions */}
      <div className={styles.sidebar}>
        <h2>Pozisyonlar</h2>
        <ul>
          {positions.map((position, index) => (
            <li key={index}>
              <button
                className={styles.positionButton}
                onClick={() => handlePositionSelect(position)}
              >
                {position}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {selectedPosition && (
          <>
            <div className={styles.exportButtons}>
              <button className={styles.exportButton} onClick={exportToExcel}>
                Excel'e Aktar
              </button>
              <button className={styles.exportButton} onClick={exportToPDF}>
                PDF'e Aktar
              </button>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.applicationsTable}>
                <thead>
                  <tr>
                    <th>Adı</th>
                    <th>Email</th>
                    <th>Telefon</th>
                    <th>Doğum Tarihi</th>
                    <th>Pozisyon</th>
                    <th>Başvuru Tarihi</th>
                    <th>CV</th>
                    <th>Sorulara Verilen Cevaplar</th>
                  </tr>
                </thead>
                <tbody>
                  {applications
                    .filter((application) => application.position === selectedPosition)
                    .map((application, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            {application.user.firstname} {application.user.lastname}
                          </td>
                          <td>{application.user.email}</td>
                          <td>{application.user.phone}</td>
                          <td>{application.user.birthdate}</td>
                          <td>{application.position}</td>
                          <td>
                            {application.timestamp.toDate().toLocaleDateString()}
                          </td>
                          <td>
                            <a
                              href={application.cvUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Görüntüle
                            </a>
                          </td>
                          <td>
                            <button
                              className={styles.expandButton}
                              onClick={() => handleToggleExpand(index)}
                            >
                              {expandedRows[index] ? 'Gizle' : 'Göster'}
                            </button>
                            {expandedRows[index] && (
                              <ul className={styles.answersList}>
                                {application.answers.map((answer, idx) => (
                                  <li key={idx}>
                                    <strong>{answer.question}:</strong> {answer.answer}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!selectedPosition && (
          <p className={styles.selectPositionMessage}>Bir pozisyon seçin</p>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;
